import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { 
  FileText, 
  Search, 
  Printer, 
  Download, 
  Eye, 
  X,
  Filter,
  Calendar,
  User,
  Building,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Loader,
  FileSpreadsheet,
  ArrowDownCircle,
  ClipboardList,
  Briefcase,
  Shield,
  Truck,
  Package,
  HardHat,
  Clock,
  Users,
  MapPin,
  Umbrella,
  Wallet
} from 'lucide-react'
import './RecordPull.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const recordTypes = [
  { id: 'leave', label: 'Leave Records', icon: Calendar, endpoint: '/leave', color: '#4caf50', 
    filterFields: ['status', 'leaveType', 'guardName'],
    statuses: ['Pending', 'Approved', 'Rejected', 'Cancelled'],
    subTypes: ['Annual', 'Sick', 'Maternity', 'Paternity', 'Compassionate', 'Emergency', 'Study', 'Unpaid'] },
  { id: 'training', label: 'Training Records', icon: CheckCircle, endpoint: '/training', color: '#2196f3',
    filterFields: ['status', 'type'],
    statuses: ['Scheduled', 'Ongoing', 'Completed', 'Cancelled'],
    subTypes: ['Induction', 'Refresher', 'Specialized', 'Workshop', 'Seminar'] },
  { id: 'shifts', label: 'Shift Records', icon: Clock, endpoint: '/shifts', color: '#ff9800',
    filterFields: ['status', 'shiftType'],
    statuses: ['Active', 'Completed', 'Cancelled', 'Pending'],
    subTypes: ['Day', 'Night', 'Swing', 'Weekend', 'Holiday'] },
  { id: 'performance', label: 'Performance Records', icon: CheckCircle, endpoint: '/performance', color: '#9c27b0',
    filterFields: ['appraisalStatus', 'reviewPeriod'],
    statuses: ['Pending', 'Reviewed', 'Approved', 'Rejected', 'Ongoing'],
    subTypes: ['Q1', 'Q2', 'Q3', 'Q4', 'Annual'] },
  { id: 'disciplinary', label: 'Disciplinary Records', icon: AlertCircle, endpoint: '/disciplinary', color: '#f44336',
    filterFields: ['status', 'incidentType', 'severity'],
    statuses: ['Open', 'Closed', 'Pending', 'Under Review'],
    subTypes: ['Absenteeism', 'Misconduct', 'Negligence', 'Insubordination', 'Theft', 'Fighting', 'Other'] },
  { id: 'incidents', label: 'Incident Reports', icon: Shield, endpoint: '/incidents', color: '#e91e63',
    filterFields: ['status', 'type', 'severity'],
    statuses: ['Open', 'Closed', 'Under Investigation', 'Resolved'],
    subTypes: ['Security', 'Safety', 'Medical', 'Fire', 'Theft', 'Vandalism', 'Other'] },
  { id: 'personnel', label: 'Recruitment/Personnel', icon: Users, endpoint: '/personnel', color: '#795548',
    filterFields: ['status', 'department'],
    statuses: ['Active', 'On Leave', 'Suspended', 'Terminated', 'Resigned'],
    subTypes: ['Security Guard', 'Supervisor', 'Manager', 'Admin', 'Finance'] },
  { id: 'offduty', label: 'Off Duty Records', icon: Umbrella, endpoint: '/offduty', color: '#607d8b',
    filterFields: ['status', 'type'],
    statuses: ['Active', 'Completed', 'Cancelled'],
    subTypes: ['Rest Day', 'Public Holiday', 'Weekend', 'Compensatory'] },
  { id: 'clientarea', label: 'Client Area Records', icon: MapPin, endpoint: '/clients', color: '#009688',
    filterFields: ['status', 'type'],
    statuses: ['Active', 'Inactive', 'Pending', 'Suspended'],
    subTypes: ['Commercial', 'Residential', 'Industrial', 'Government', 'Event'] },
  { id: 'guards', label: 'Guard Records', icon: Shield, endpoint: '/guards', color: '#3f51b5',
    filterFields: ['status', 'type', 'rank'],
    statuses: ['Active', 'On Leave', 'Suspended', 'Terminated', 'Resigned'],
    subTypes: ['Security Guard', 'Supervisor', 'Manager', 'Team Leader', 'Commander'] },
  { id: 'advancepayments', label: 'Advance Payment Records', icon: Wallet, endpoint: '/advance-payments', color: '#e91e63',
    filterFields: ['status', 'type'],
    statuses: ['Pending', 'Approved', 'Issued', 'Rejected', 'Partially Paid'],
    subTypes: ['Personnel', 'Guard'] },
  { id: 'inventory', label: 'Inventory Records', icon: Package, endpoint: '/inventory', color: '#795548',
    filterFields: ['status', 'type'],
    statuses: ['Active', 'Inactive', 'Low Stock', 'Out of Stock'],
    subTypes: ['Uniform', 'Stationery', 'Equipment', 'Safety Gear', 'Other'] },
  { id: 'assets', label: 'Assets Inventory', icon: Package, endpoint: '/records/assets', color: '#607d8b',
    filterFields: ['status', 'category', 'condition'],
    statuses: ['Active', 'In Repair', 'Disposed', 'Lost', 'Damaged'],
    subTypes: ['Vehicle', 'Weapon', 'Radio', 'Computer', 'Furniture', 'Other'] },
  { id: 'uniforms', label: 'Uniforms Records', icon: Shield, endpoint: '/records/uniforms', color: '#3f51b5',
    filterFields: ['status', 'type', 'size'],
    statuses: ['Issued', 'Returned', 'Damaged', 'Lost', 'In Stock'],
    subTypes: ['Boots', 'Trousers', 'Shirt', 'Jacket', 'Cap', 'Belt', 'Other'] },
  { id: 'audits', label: 'Audit Reports', icon: CheckCircle, endpoint: '/records/audits', color: '#ff9800',
    filterFields: ['status', 'type', 'department'],
    statuses: ['Pending', 'In Progress', 'Completed', 'Overdue'],
    subTypes: ['Internal', 'External', 'Compliance', 'Financial', 'Operational'] },
  { id: 'guardtracking', label: 'Guard Tracking', icon: MapPin, endpoint: '/records/guard-tracking', color: '#4caf50',
    filterFields: ['status', 'shift', 'site'],
    statuses: ['Active', 'Off Duty', 'On Leave', 'Suspended'],
    subTypes: ['Patrol', 'Static', 'Escort', 'Event', 'Emergency'] },
  { id: 'policies', label: 'Policy Documents', icon: FileText, endpoint: '/records/policies', color: '#2196f3',
    filterFields: ['status', 'type', 'department'],
    statuses: ['Active', 'Draft', 'Under Review', 'Archived', 'Expired'],
    subTypes: ['Security', 'HR', 'Operations', 'Finance', 'Safety', 'Compliance'] },
]

const RecordPull = () => {
  const [selectedType, setSelectedType] = useState('')
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [subTypeFilter, setSubTypeFilter] = useState('')
  const [viewRecord, setViewRecord] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 4000)
  }

  const fetchRecords = async (typeId, filters = {}) => {
    if (!typeId) return
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const typeConfig = recordTypes.find(t => t.id === typeId)
      
      // Build query params for backend filtering
      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.subType) params.append('type', filters.subType)
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
      if (filters.dateTo) params.append('dateTo', filters.dateTo)
      
      const queryString = params.toString() ? `?${params.toString()}` : ''
      
      let res = await axios.get(`${API_URL}${typeConfig.endpoint}${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      const data = res?.data?.data || res?.data || []
      setRecords(Array.isArray(data) ? data : [])
      showMessage('success', `${typeConfig.label} loaded: ${data.length} records`)
    } catch (err) {
      console.error('Fetch error:', err)
      setRecords([])
      showMessage('error', 'Failed to load records')
    } finally {
      setLoading(false)
    }
  }

  const handleTypeChange = (typeId) => {
    setSelectedType(typeId)
    setRecords([])
    setViewRecord(null)
    setSearchTerm('')
    setStatusFilter('')
    setSubTypeFilter('')
    setDateFrom('')
    setDateTo('')
    setDropdownOpen(false)
    fetchRecords(typeId)
  }

  const applyFilters = () => {
    fetchRecords(selectedType, {
      status: statusFilter,
      subType: subTypeFilter,
      dateFrom: dateFrom,
      dateTo: dateTo
    })
  }

  const filteredRecords = records.filter(r => {
    const search = searchTerm.toLowerCase()
    return JSON.stringify(r).toLowerCase().includes(search)
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  const formatValue = (val) => {
    if (val === null || val === undefined) return 'N/A'
    if (typeof val === 'boolean') return val ? 'Yes' : 'No'
    if (typeof val === 'object') return JSON.stringify(val).substring(0, 50)
    return String(val)
  }

  const handleBulkExcelExport = () => {
    if (filteredRecords.length === 0) {
      showMessage('error', 'No records to export')
      return
    }
    
    const typeConfig = recordTypes.find(t => t.id === selectedType)
    const allKeys = new Set()
    filteredRecords.forEach(r => {
      Object.keys(r).forEach(k => {
        if (!k.startsWith('_') && k !== '__v') allKeys.add(k)
      })
    })
    const headers = Array.from(allKeys)
    
    const rows = filteredRecords.map(record => {
      return headers.map(h => {
        const val = record[h]
        if (val === null || val === undefined) return ''
        if (typeof val === 'object') return `"${JSON.stringify(val).replace(/"/g, '""')}"`
        return `"${String(val).replace(/"/g, '""')}"`
      })
    })
    
    const headerLabels = headers.map(h => 
      h.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    )
    
    const csv = [headerLabels.join(','), ...rows.map(r => r.join(','))].join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    
    let filename = `${typeConfig.id}_records`
    if (statusFilter) filename += `_status-${statusFilter}`
    if (subTypeFilter) filename += `_type-${subTypeFilter}`
    if (dateFrom) filename += `_from-${dateFrom}`
    if (dateTo) filename += `_to-${dateTo}`
    filename += `_${new Date().toISOString().split('T')[0]}.csv`
    
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    showMessage('success', `${filteredRecords.length} records exported to Excel`)
  }

  const handlePrint = (record) => {
    const typeConfig = recordTypes.find(t => t.id === selectedType)
    const w = window.open('', '_blank')
    const printContent = `
      <html>
        <head>
          <title>${typeConfig.label} - ${record._id || 'Record'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333; }
            .header { text-align: center; border-bottom: 3px solid ${typeConfig.color}; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { color: ${typeConfig.color}; margin: 0; font-size: 24px; }
            .header h2 { margin: 10px 0 0; font-size: 18px; color: #666; font-weight: normal; }
            .record-header { background: #f5f5f5; padding: 15px 20px; border-radius: 8px; margin-bottom: 25px; }
            .record-header h3 { margin: 0 0 8px; color: ${typeConfig.color}; }
            .record-header p { margin: 4px 0; color: #666; font-size: 14px; }
            .section { margin-bottom: 25px; }
            .section h4 { color: ${typeConfig.color}; border-bottom: 2px solid #eee; padding-bottom: 8px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #f9f9f9; font-weight: 600; color: #555; width: 35%; }
            td { color: #333; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; color: #999; font-size: 12px; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; }
            .no-print { display: none; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Gismac Security Limited</h1>
            <h2>${typeConfig.label}</h2>
          </div>
          <div class="record-header">
            <h3>Record Reference: ${record._id || 'N/A'}</h3>
            <p><strong>Generated:</strong> ${new Date().toLocaleDateString('en-KE', {day:'2-digit', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit'})}</p>
            <p><strong>Status:</strong> <span class="status-badge">${record.status || record.appraisalStatus || record.leaveStatus || 'N/A'}</span></p>
          </div>
          <div class="section">
            <h4>Record Details</h4>
            <table>
              ${Object.entries(record).filter(([key]) => !key.startsWith('_') && key !== '__v').map(([key, val]) => `
                <tr>
                  <th>${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</th>
                  <td>${formatValueForPrint(val)}</td>
                </tr>
              `).join('')}
            </table>
          </div>
          <div class="footer">
            <p>This document was generated from the Gismac Security Management System</p>
            <p>www.secs.gismac.co.ke | Document ID: ${record._id || 'N/A'}</p>
          </div>
          <div style="text-align:center; margin-top:30px;">
            <button onclick="window.print()" style="padding:12px 30px; background:${typeConfig.color}; color:white; border:none; border-radius:6px; cursor:pointer; font-size:16px;">
              Print Document
            </button>
          </div>
        </body>
      </html>
    `
    w.document.write(printContent)
    w.document.close()
  }

  const handleDownloadPDF = (record) => {
    const typeConfig = recordTypes.find(t => t.id === selectedType)
    const content = `
Gismac Security Limited - ${typeConfig.label}
===============================================

Record ID: ${record._id || 'N/A'}
Generated: ${new Date().toLocaleString('en-KE')}
Status: ${record.status || record.appraisalStatus || record.leaveStatus || 'N/A'}

RECORD DETAILS:
${Object.entries(record).filter(([key]) => !key.startsWith('_') && key !== '__v').map(([key, val]) => {
  const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
  let value = val
  if (val && typeof val === 'object') value = JSON.stringify(val)
  return `${label}: ${value || 'N/A'}`
}).join('\n')}

===============================================
Generated by Gismac Security Management System
www.secs.gismac.co.ke
    `.trim()

    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${typeConfig.id}_${record._id || 'record'}_${new Date().toISOString().split('T')[0]}.txt`
    a.click()
    window.URL.revokeObjectURL(url)
    showMessage('success', 'Document downloaded')
  }

  const formatValueForPrint = (val) => {
    if (val === null || val === undefined) return 'N/A'
    if (typeof val === 'boolean') return val ? 'Yes' : 'No'
    if (val instanceof Date) return val.toLocaleDateString('en-KE')
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val, null, 2).replace(/\n/g, '<br>').replace(/ /g, '&nbsp;')
      } catch {
        return String(val)
      }
    }
    return String(val)
  }

  const getRecordTitle = (record) => {
    if (!record) return 'Untitled Record'
    const meaningfulFields = ['title', 'name', 'guardName', 'employeeName', 'companyName', 'site', 'description', 'type', 'category', 'reason']
    for (const field of meaningfulFields) {
      if (record[field] && typeof record[field] === 'string' && record[field].trim()) {
        return record[field].trim()
      }
    }
    const typeLabel = selectedType ? selectedType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Record'
    const dateStr = record.date ? new Date(record.date).toLocaleDateString() : ''
    return `${typeLabel}${dateStr ? ' - ' + dateStr : ''}`
  }

  const getRecordSubtitle = (record) => {
    if (!record) return ''
    const subtitleFields = ['description', 'findings', 'recommendations', 'status', 'type', 'category', 'auditor', 'leaveType', 'incidentType']
    for (const field of subtitleFields) {
      if (record[field] && typeof record[field] === 'string' && record[field].trim()) {
        return record[field].trim().substring(0, 100) + (record[field].length > 100 ? '...' : '')
      }
    }
    return ''
  }

  const getStatusBadgeClass = (status) => {
    const s = (status || 'pending').toLowerCase()
    if (['approved', 'active', 'completed', 'resolved'].includes(s)) return 'status-success'
    if (['pending', 'scheduled', 'ongoing', 'under review'].includes(s)) return 'status-warning'
    if (['rejected', 'cancelled', 'terminated', 'suspended'].includes(s)) return 'status-danger'
    return 'status-default'
  }

  const selectedTypeConfig = recordTypes.find(t => t.id === selectedType)

  return (
    <div className="record-pull">
      <div className="rp-header">
        <div>
          <h1><ClipboardList size={28} /> Record Pull Center</h1>
          <p>Request, view, download and print any system record</p>
        </div>
      </div>

      {message.text && (
        <div className={`rp-alert rp-alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Modern Dropdown Record Type Selector */}
      <div className="rp-dropdown-container">
        <label className="rp-label"><Filter size={16} /> Select Record Type:</label>
        <div className="rp-dropdown">
          <button 
            className="rp-dropdown-toggle" 
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selectedTypeConfig ? (
              <>
                <selectedTypeConfig.icon size={18} style={{ color: selectedTypeConfig.color }} />
                <span>{selectedTypeConfig.label}</span>
              </>
            ) : (
              <><FileText size={18} /><span>Select Record Type...</span></>
            )}
            <ChevronDown size={16} className={`rp-chevron ${dropdownOpen ? 'open' : ''}`} />
          </button>
          
          {dropdownOpen && (
            <div className="rp-dropdown-menu">
              {recordTypes.map(type => {
                const Icon = type.icon
                return (
                  <button
                    key={type.id}
                    className={`rp-dropdown-item ${selectedType === type.id ? 'active' : ''}`}
                    onClick={() => handleTypeChange(type.id)}
                  >
                    <Icon size={18} style={{ color: type.color }} />
                    <span>{type.label}</span>
                    {selectedType === type.id && <CheckCircle size={14} style={{ color: type.color }} />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {selectedType && (
        <div className="rp-advanced-filters">
          <div className="rp-filter-row">
            <div className="rp-search-box">
              <Search size={16} />
              <input 
                type="text" 
                placeholder="Search records..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="rp-filter-group">
              <label><Filter size={12} /> Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                {selectedTypeConfig?.statuses?.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            
            <div className="rp-filter-group">
              <label><Briefcase size={12} /> Type</label>
              <select value={subTypeFilter} onChange={(e) => setSubTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                {selectedTypeConfig?.subTypes?.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            
            <div className="rp-filter-group">
              <label><Calendar size={12} /> From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            
            <div className="rp-filter-group">
              <label><Calendar size={12} /> To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            
            <button className="rp-btn-apply" onClick={applyFilters}>
              <Filter size={14} /> Apply
            </button>
          </div>
          
          {/* Bulk Export Button */}
          <div className="rp-bulk-actions">
            <button 
              className="rp-btn-bulk-excel" 
              onClick={handleBulkExcelExport}
              disabled={filteredRecords.length === 0}
            >
              <FileSpreadsheet size={16} />
              Export {filteredRecords.length} Records to Excel
            </button>
            <span className="rp-filter-summary">
              Showing {filteredRecords.length} of {records.length} records
              {statusFilter && ` • Status: ${statusFilter}`}
              {subTypeFilter && ` • Type: ${subTypeFilter}`}
            </span>
          </div>
        </div>
      )}

      {/* Records List */}
      {loading ? (
        <div className="rp-loading">
          <Loader size={32} className="rp-spin" />
          <p>Loading records...</p>
        </div>
      ) : selectedType && filteredRecords.length === 0 ? (
        <div className="rp-empty">
          <FileText size={48} opacity={0.3} />
          <p>No records found</p>
          <span>Try adjusting your search or filters</span>
        </div>
      ) : selectedType ? (
        <div className="rp-records-list">
          <div className="rp-modern-cards">
            {filteredRecords.map(record => {
              const typeConfig = recordTypes.find(t => t.id === selectedType)
              const recordStatus = record.status || record.appraisalStatus || record.leaveStatus || 'Pending'
              return (
                <div key={record._id} className="rp-modern-card" style={{ borderLeftColor: typeConfig.color }}>
                  <div className="rp-card-main">
                    <div className="rp-card-icon" style={{ backgroundColor: typeConfig.color + '15', color: typeConfig.color }}>
                      <typeConfig.icon size={24} />
                    </div>
                    <div className="rp-card-info">
                      <h4>{getRecordTitle(record)}</h4>
                      <div className="rp-card-tags">
                        <span className={`rp-status-badge ${getStatusBadgeClass(recordStatus)}`}>
                          {recordStatus}
                        </span>
                        {(record.leaveType || record.type || record.incidentType) && (
                          <span className="rp-type-badge">
                            {record.leaveType || record.type || record.incidentType}
                          </span>
                        )}
                      </div>
                      <div className="rp-card-meta">
                        <span><Calendar size={12} /> {formatDate(record.startDate || record.date || record.createdAt || record.incidentDate)}</span>
                        <span><User size={12} /> {record.guardName || record.employeeName || record.personnelName || record.requestedBy || 'System'}</span>
                        {record.site && <span><Building size={12} /> {record.site}</span>}
                      </div>
                      <p className="rp-card-desc">{getRecordSubtitle(record)}</p>
                    </div>
                  </div>
                  <div className="rp-card-actions-modern">
                    <button className="rp-action-btn rp-action-view" onClick={() => setViewRecord(record)} title="View">
                      <Eye size={16} />
                      <span>View</span>
                    </button>
                    <button className="rp-action-btn rp-action-print" onClick={() => handlePrint(record)} title="Print">
                      <Printer size={16} />
                      <span>Print</span>
                    </button>
                    <button className="rp-action-btn rp-action-download" onClick={() => handleDownloadPDF(record)} title="Download">
                      <Download size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="rp-empty">
          <FileText size={48} opacity={0.3} />
          <p>Select a record type from the dropdown to get started</p>
        </div>
      )}

      {/* View Modal */}
      {viewRecord && (
        <div className="rp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewRecord(null)}>
          <div className="rp-modal">
            <div className="rp-modal-header">
              <h3><Eye size={20} /> Record Details</h3>
              <button className="rp-btn-close" onClick={() => setViewRecord(null)}><X size={20} /></button>
            </div>
            <div className="rp-modal-body">
              <div className="rp-detail-section">
                <h4>Information</h4>
                <div className="rp-detail-grid">
                  {Object.entries(viewRecord).filter(([key]) => !key.startsWith('_') && key !== '__v').map(([key, val]) => (
                    <div key={key} className="rp-detail-item">
                      <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                      <span>{formatValue(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rp-modal-footer">
              <button className="rp-btn rp-btn-secondary" onClick={() => setViewRecord(null)}>
                <X size={14} /> Close
              </button>
              <button className="rp-btn rp-btn-print" onClick={() => handlePrint(viewRecord)}>
                <Printer size={14} /> Print
              </button>
              <button className="rp-btn rp-btn-download" onClick={() => handleDownloadPDF(viewRecord)}>
                <Download size={14} /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecordPull
